import {
  Card,
  Stack,
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Fade,
  IconButton,
  Tooltip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Page from "../Page";
import { FEATURE } from "src/util/enums";
import { useAccess } from "src/hooks/useAccess.hook";
import { useList } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { BaseTable } from "../BaseTable/BaseTable";
import { customerListColumns } from "./customer.constant";
import { commonSlice } from "src/store/common/common.slice";
import { useNavigate } from "react-router-dom";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { Icon } from "@iconify/react";
import peopleFill from "@iconify/icons-eva/people-fill";
import personAddFill from "@iconify/icons-eva/person-add-fill";
import checkmarkCircle2Fill from "@iconify/icons-eva/checkmark-circle-2-fill";
import closeCircleFill from "@iconify/icons-eva/close-circle-fill";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import calendarFill from "@iconify/icons-eva/calendar-fill";
import { alertSlice } from "src/store/alert/alert.slice";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'visible',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.palette.primary.light}20`,
    border: `1px solid ${theme.palette.primary.light}60`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));


export default function UserList() {
  const hasCreateAccess = useAccess(FEATURE.CUSTOMERS, "C");
  const hasViewAccess = useAccess(FEATURE.CUSTOMERS, "V");
  const { data: customers, isFetching, refetch } = useList("customers");
  const hasUpdateAccess = useAccess(FEATURE.CUSTOMERS, "U");
  const isReadOnly = hasViewAccess && !hasUpdateAccess;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const { access } = useSelectedTeam();

  // Redirect if user doesn't have view access (only after team data is loaded)
  useEffect(() => {
    if (access && !hasViewAccess) {
      dispatch(
        alertSlice.actions.setAlert({ open: true, message: "Not Authorized", severity: "error" })
      );
      navigate("/dashboard", { replace: true });
    }
  }, [hasViewAccess, access, dispatch, navigate]);

  // Calculate statistics
  const totalDrivers = customers?.length || 0;
  const activeDrivers = customers?.filter(customer => customer?.active === "Y")?.length || 0;
  const inactiveDrivers = customers?.filter(customer => customer?.active === "N")?.length || 0;

  const handleRefresh = () => {
    refetch();
  };

  const rowOrCellClickRedirect = ({ id }) => {
    if (!hasViewAccess) return;
    dispatch(tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
      detailsId: id,
      tabOpenedFor: "customer",
      tabOpened: "general"
    }))
    navigate(`/customer/${id}`);
  };

  const addNewCustomerBtnHandler = () => dispatch(tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
    detailsId: "",
    tabOpenedFor: "customer",
    tabOpened: "general"
  }))

  useEffect(() => {
    if (!customers) return;

    if (searchValue) {
      const searchVal = searchValue.toLowerCase();
      const filteredDataArr = customers.reduce((filteredDataArr, currentObj) => {
        const relevantFields = [
          "firstName",
          "lastName",
          "email",
          "phone",
          "active",
          "createdAt",
        ];

        const allValuesArr = Object.entries(currentObj)
          .filter(([key]) => relevantFields.includes(key))
          .map(([key, value]) => {
            if (key === "createdAt") {
              return yearFirstDateTimeFormat(value).toString().toLowerCase();
            } else if (key === "firstName" || key === "lastName") {
              return `${currentObj.firstName} ${currentObj.lastName}`.toString().toLowerCase();
            } else {
              return value === "Y" ? "enabled" : value === "N" ? "disabled" : value ? value.toString().toLowerCase() : "";
            }
          });

        const searchResult = allValuesArr.find((value) => value.includes(searchVal));
        if (searchResult) {
          filteredDataArr = [...filteredDataArr, currentObj];
        }
        return filteredDataArr;
      }, []);
      setTableData(filteredDataArr);
    } else {
      setTableData([...customers]);
    }
  }, [customers, searchValue]);

  const handleSearching = (event) => {
    setSearchValue(event.target.value.trim());
  };

  const LoadingSkeleton = () => (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5].map((item) => (
          <Grid item xs={12} key={item}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
              </Box>
              <Skeleton variant="rectangular" width={80} height={32} />
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const EmptyState = () => (
    <Fade in>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        textAlign: 'center'
      }}>
        <Icon icon={peopleFill} width={64} height={64} style={{ color: '#ccc', marginBottom: 16 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
          No EV Drivers Found
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {searchValue
            ? 'No drivers match your search criteria. Try adjusting your search terms.'
            : 'Get started by adding your first EV driver to manage and monitor your electric vehicle users.'
          }
        </Typography>
      </Box>
    </Fade>
  );

  return (
    <Page
      {...(hasCreateAccess && { path: `/customer/new`, action: addNewCustomerBtnHandler })}
      actionButtonText={hasCreateAccess ? "Add EV Driver" : ""}
    >
      <StyledListCard>
        {/* Header Section with Statistics */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              {/* <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
                EV Drivers
              </Typography> */}
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Manage and monitor electric vehicle drivers
              </Typography>
            </Box>
            <Tooltip title="Refresh data">
              <span>
                <IconButton
                  onClick={handleRefresh}
                  disabled={isFetching}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '&:disabled': {
                      bgcolor: 'grey.300',
                      color: 'grey.500',
                    },
                  }}
                >
                  <Icon icon={refreshFill} width={20} height={20} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StyledListCard>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
                      {isFetching ? <Skeleton width={40} /> : totalDrivers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Drivers
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon icon={peopleFill} width={32} height={32} color="white" />
                  </Box>
                </Stack>
              </StyledListCard>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <StyledListCard>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
                      {isFetching ? <Skeleton width={40} /> : activeDrivers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Drivers
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon icon={checkmarkCircle2Fill} width={32} height={32} color="white" />
                  </Box>
                </Stack>
              </StyledListCard>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <StyledListCard>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
                      {isFetching ? <Skeleton width={40} /> : inactiveDrivers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Inactive Drivers
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon icon={closeCircleFill} width={32} height={32} color="white" />
                  </Box>
                </Stack>
              </StyledListCard>
            </Grid>
          </Grid>
        </Box>

        {/* Search and Filters Section */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Search & Filter
            </Typography>
            {searchValue && (
              <Chip
                label={`${tableData?.length || 0} results`}
                color="primary"
                size="small"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Stack>
          <CommonSearchbar
            placeholder="Search drivers by name, email, phone, or status..."
            searchFunc={(e) => handleSearching(e)}
          />
        </Box>

        {/* Data Table */}
        <BaseTable
          rows={tableData || []}
          columns={customerListColumns(isReadOnly)}
          loading={isFetching}
          getRowId={(row) => row?.id || row?.customer_id || Math.random()}
          pageSize={10}
          isReadOnly={isReadOnly}
          showExportCsvBtn={false}
          csvMorphemes="ev_drivers"
          csvColumns={customerListColumns(isReadOnly)}
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: isReadOnly ? 'default' : 'pointer',
              '&:hover': {
                backgroundColor: isReadOnly ? 'transparent' : 'action.hover',
              },
            },
            '& .MuiDataGrid-cell': {
              cursor: isReadOnly ? 'default' : 'pointer',
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
              borderBottom: isReadOnly ? 'none' : '1px solid',
              borderBottomColor: 'primary.light',
            },
          }}
        />
      </StyledListCard>
    </Page>
  );
}
